// PhoneEditModal — uses Radix Themes <Dialog> so the padding, radius,
// shadow and animation tokens match the WorkOS UserProfile widget's own
// edit modals (it uses the same Dialog primitives internally). Radix
// Dialog handles portaling, overlay, ESC, focus trap and body scroll
// lock for us, so this component only has to own the form logic.
//
// UK phone validation is applied on blur; Save is disabled while the
// input is invalid or a save is in flight.

import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Dialog, Flex, Strong, Text, TextField } from '@radix-ui/themes'
import { updateProfile } from '../../auth/services/auth-api'
import { useToast } from '../radix'

// UK phone validation (mobile + landline). Whitespace is stripped before
// matching, so the pattern itself is space-free. Rejects non-UK numbers.
const UK_PHONE_REGEX = /^(?:\+44|0044|0)[1-9]\d{8,9}$/

interface PhoneEditModalProps {
  open: boolean
  initialValue: string
  onClose: () => void
  onSaved?: (phone: string) => void
}

export function PhoneEditModal({
  open,
  initialValue,
  onClose,
  onSaved,
}: PhoneEditModalProps) {
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  const [value, setValue] = useState(initialValue)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // Reset state whenever modal is opened with a (possibly new) initial value
  useEffect(() => {
    if (open) {
      setValue(initialValue)
      setError('')
    }
  }, [open, initialValue])

  const validate = (v: string): string => {
    if (!v.trim()) {
      return initialValue
        ? 'Phone number is required and cannot be removed'
        : ''
    }
    // Strip internal whitespace before matching so users can type the
    // number in any common format (e.g. "+44 20 3322 9128").
    if (!UK_PHONE_REGEX.test(v.replace(/\s+/g, ''))) {
      return 'Enter a valid UK phone number (e.g. 07700 900000 or 020 3322 9128)'
    }
    return ''
  }

  const handleSave = async () => {
    const msg = validate(value)
    if (msg) {
      setError(msg)
      return
    }
    setSaving(true)
    try {
      await updateProfile({ phone: value.trim() })
      await queryClient.invalidateQueries({ queryKey: ['auth-me'] })
      addToast('Phone number updated', 'success', 2000)
      onSaved?.(value.trim())
      onClose()
    } catch (err: any) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to save phone number'
      setError(message)
      addToast(message, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Block outside-click / ESC close while a save is in flight so
        // the user can't dismiss mid-request and lose feedback.
        if (!next && !saving) onClose()
      }}
    >
      <Dialog.Content maxWidth="480px">
        <Dialog.Title mb="5">Edit phone</Dialog.Title>

        <Flex direction="column" gap="2">
          <Text as="label" htmlFor="phone-modal-input" size="2">
            <Strong>Phone number</Strong>
          </Text>
          <TextField.Root
            id="phone-modal-input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="e.g. 07700 900000"
            value={value}
            aria-invalid={!!error}
            color={error ? 'red' : undefined}
            onChange={(e) => {
              setValue(e.target.value)
              if (error) setError('')
            }}
            onBlur={() => setError(validate(value))}
            autoFocus
          />
          <Text size="1" color={error ? 'red' : 'gray'}>
            {error || 'UK phone (mobile or landline). Accepts 07…, 020…, +44…, etc.'}
          </Text>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Button
            variant="surface"
            color="gray"
            size="2"
            onClick={onClose}
            disabled={saving}
            className="no-hover"
            style={{ color: 'var(--gray-12)' }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            size="2"
            onClick={handleSave}
            disabled={saving || !!error}
            className="no-hover"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
